import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { MILESTONES, milestoneProgress, overallPercent } from "@/constants/milestones";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/hq/useDraft";
import { toast } from "sonner";

const STATUSES = [
  "AVAILABLE",
  "COMING_SOON",
  "WAITLIST",
  "ENROLLMENT_OPEN",
  "ENROLLMENT_CLOSED",
];

const Select = ({ value, onChange, options, testId }) => (
  <select
    data-testid={testId}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    className="mono w-full rounded-sm border border-border bg-[#0A0A0A] px-3 py-2 text-xs text-white"
  >
    {options.map((o) => (
      <option key={o} value={o}>{o}</option>
    ))}
  </select>
);

const ProductRow = ({ product, onSaved }) => {
  const [draft, setDraft] = useState(product);
  const [dirty, setDirty] = useState(false);
  const set = (k) => (v) => {
    setDraft({ ...draft, [k]: v });
    setDirty(true);
  };

  const save = async () => {
    try {
      const { data } = await api.put(`/products/${product.key}`, { data: draft });
      setDraft(data);
      setDirty(false);
      onSaved(data);
      toast.success(`${data.name} saved`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div
      data-testid={`hq-product-${product.key}`}
      className="border border-border bg-[#141414] p-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="mono text-[10px] tracking-widest text-muted-foreground">
          LEVEL {product.level} · {product.tier.toUpperCase()} · {product.key}
        </div>
        <Button
          data-testid={`hq-product-save-${product.key}`}
          onClick={save}
          className="display rounded-sm bg-primary text-base uppercase text-primary-foreground hover:bg-[#E6C800]"
        >
          {dirty ? "Save" : "Saved"}
        </Button>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Field label="NAME">
          <Input data-testid={`hq-product-name-${product.key}`} value={draft.name || ""}
            onChange={(e) => set("name")(e.target.value)} className="rounded-sm bg-[#0A0A0A]" />
        </Field>
        <Field label="PRICE LABEL (SHOWN ON CARD)">
          <Input data-testid={`hq-product-price-label-${product.key}`} value={draft.price_label || ""}
            onChange={(e) => set("price_label")(e.target.value)} className="rounded-sm bg-[#0A0A0A]" />
        </Field>
        <Field label="PRICE (USD)">
          <Input type="number" value={draft.price ?? ""}
            onChange={(e) => set("price")(e.target.value === "" ? null : Number(e.target.value))}
            className="rounded-sm bg-[#0A0A0A]" />
        </Field>
        <Field label="SALE PRICE (USD)">
          <Input type="number" value={draft.sale_price ?? ""}
            onChange={(e) => set("sale_price")(e.target.value === "" ? null : Number(e.target.value))}
            className="rounded-sm bg-[#0A0A0A]" />
        </Field>
        <Field label="STATUS">
          <Select testId={`hq-product-status-${product.key}`} value={draft.status}
            onChange={set("status")} options={STATUSES} />
        </Field>
        <Field label="SORT ORDER">
          <Input type="number" value={draft.order ?? 0}
            onChange={(e) => set("order")(Number(e.target.value))} className="rounded-sm bg-[#0A0A0A]" />
        </Field>
        <Field label="CTA WORDING">
          <Input data-testid={`hq-product-cta-${product.key}`} value={draft.cta_text || ""}
            onChange={(e) => set("cta_text")(e.target.value)} className="rounded-sm bg-[#0A0A0A]" />
        </Field>
        <Field label="CTA DESTINATION (PATH OR 'checkout')">
          <Input value={draft.cta_destination || ""}
            onChange={(e) => set("cta_destination")(e.target.value)} className="rounded-sm bg-[#0A0A0A]" />
        </Field>
        <Field label="ENROLLMENT OPENS (ISO DATE)">
          <Input placeholder="2026-07-01T00:00:00+00:00" value={draft.enroll_open_at || ""}
            onChange={(e) => set("enroll_open_at")(e.target.value || null)} className="rounded-sm bg-[#0A0A0A]" />
        </Field>
        <Field label="ENROLLMENT CLOSES (ISO DATE)">
          <Input placeholder="2026-08-01T00:00:00+00:00" value={draft.enroll_close_at || ""}
            onChange={(e) => set("enroll_close_at")(e.target.value || null)} className="rounded-sm bg-[#0A0A0A]" />
        </Field>
        <Field label="PRODUCT IMAGE PATH">
          <Input value={draft.image || ""} onChange={(e) => set("image")(e.target.value)}
            className="rounded-sm bg-[#0A0A0A]" />
        </Field>
        <Field label="FEATURED">
          <Select value={String(!!draft.featured)} onChange={(v) => set("featured")(v === "true")}
            options={["false", "true"]} />
        </Field>
      </div>

      <div className="mt-4 grid gap-4">
        <Field label="HEADLINE">
          <Input value={draft.headline || ""} onChange={(e) => set("headline")(e.target.value)}
            className="rounded-sm bg-[#0A0A0A]" />
        </Field>
        <Field label="DESCRIPTION">
          <Textarea value={draft.description || ""} onChange={(e) => set("description")(e.target.value)}
            className="rounded-sm bg-[#0A0A0A]" rows={3} />
        </Field>
        <Field label="FEATURES (ONE PER LINE)">
          <Textarea value={(draft.features || []).join("\n")}
            onChange={(e) => set("features")(e.target.value.split("\n").filter(Boolean))}
            className="rounded-sm bg-[#0A0A0A]" rows={6} />
        </Field>
        <Field label="NOTE / CALLOUT">
          <Textarea value={draft.note || ""} onChange={(e) => set("note")(e.target.value)}
            className="rounded-sm bg-[#0A0A0A]" rows={2} />
        </Field>
        <Field label="BEST FOR (COMPARISON SECTION)">
          <Input value={draft.best_for || ""} onChange={(e) => set("best_for")(e.target.value)}
            className="rounded-sm bg-[#0A0A0A]" />
        </Field>
      </div>
    </div>
  );
};

export const ProductsEditor = () => {
  const [products, setProducts] = useState(null);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data.products));
  }, []);

  if (!products) return <div className="mono text-sm text-muted-foreground">Loading offers…</div>;

  return (
    <div className="space-y-5" data-testid="hq-products">
      {products.map((p) => (
        <ProductRow
          key={p.key}
          product={p}
          onSaved={(next) =>
            setProducts((prev) => prev.map((x) => (x.key === next.key ? next : x)))
          }
        />
      ))}
    </div>
  );
};

const SOURCE_LABEL = {
  free_starter_guide: "Free Starter Guide",
  event_calculator: "Event Calculator",
  popup_profits_waitlist: "Pop-Up Profits Waitlist",
  smash_academy_waitlist: "Smash Academy Waitlist",
  operator_application: "Operator Application",
};

const WaitlistCounter = ({ waitlist }) => {
  if (!waitlist) return null;
  const pct = Math.min((waitlist.count / waitlist.goal) * 100, 100);
  return (
    <div data-testid="hq-waitlist-counter" className="mb-8 border border-primary/60 bg-[#141414] p-6">
      <div className="mono text-[10px] tracking-[0.3em] text-primary">FOUNDING 25 WAITLIST</div>
      <div className="mt-2 flex flex-wrap items-end gap-3">
        <span data-testid="hq-waitlist-count" className="display text-5xl leading-none text-primary">
          {waitlist.count}
        </span>
        <span className="display text-2xl leading-none text-muted-foreground">
          / {waitlist.goal}
        </span>
        <span className="mono mb-1 text-[10px] tracking-widest text-muted-foreground">
          {waitlist.remaining
            ? `${waitlist.remaining} SPOTS LEFT TO HIT THE GOAL`
            : "GOAL HIT — START THE FOUNDING LAUNCH"}
        </span>
      </div>
      <div className="mt-4 h-2 w-full bg-[#0A0A0A]">
        <div
          data-testid="hq-waitlist-bar"
          className="h-2 bg-primary transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
      {waitlist.recent?.length ? (
        <div className="mono mt-4 space-y-1 text-[10px] tracking-widest text-muted-foreground">
          {waitlist.recent.map((r) => (
            <div key={r.email} data-testid={`hq-waitlist-recent-${r.email}`}>
              {r.joined} — {(r.name || r.email).toUpperCase()}
            </div>
          ))}
        </div>
      ) : (
        <p className="mono mt-4 text-[10px] tracking-widest text-muted-foreground">
          NOBODY ON THE WAITLIST YET. SHARE THE ACADEMY LINK AND THIS FILLS UP.
        </p>
      )}
    </div>
  );
};

export const LeadsDashboard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/leads")
      .then(({ data }) => setData(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message));
  }, []);

  const setStatus = async (lead, status) => {
    await api.put(`/leads/${lead.id}/status`, { status });
    setData((prev) => ({
      ...prev,
      leads: prev.leads.map((l) => (l.id === lead.id ? { ...l, status } : l)),
    }));
    toast.success("Lead status updated");
  };

  if (error) return <div className="text-sm text-destructive">{error}</div>;
  if (!data) return <div className="mono text-sm text-muted-foreground">Loading leads…</div>;
  if (!data.leads.length)
    return (
      <>
        <WaitlistCounter waitlist={data.waitlist} />
        <div data-testid="hq-leads-empty" className="mono text-sm text-muted-foreground">
          No leads yet. They show up the moment someone grabs the free guide.
        </div>
      </>
    );

  return (
    <div data-testid="hq-leads">
      <WaitlistCounter waitlist={data.waitlist} />
      <div className="overflow-x-auto">
      <table className="w-full min-w-[980px] border-collapse text-sm">
        <thead>
          <tr className="mono border-b border-border text-left text-[10px] tracking-widest text-muted-foreground">
            <th className="py-3 pr-4">NAME</th>
            <th className="py-3 pr-4">EMAIL</th>
            <th className="py-3 pr-4">JOINED</th>
            <th className="py-3 pr-4">SOURCE</th>
            <th className="py-3 pr-4">PURCHASED</th>
            <th className="py-3 pr-4">HIGHEST OFFER</th>
            <th className="py-3 pr-4">SEQUENCE</th>
            <th className="py-3 pr-4">OPERATOR</th>
            <th className="py-3">STATUS</th>
          </tr>
        </thead>
        <tbody>
          {data.leads.map((l) => (
            <tr key={l.id} data-testid={`hq-lead-${l.email}`} className="border-b border-border/60">
              <td className="py-3 pr-4">{[l.first_name, l.last_name].filter(Boolean).join(" ")}</td>
              <td className="py-3 pr-4 text-muted-foreground">{l.email}</td>
              <td className="mono py-3 pr-4 text-[11px] text-muted-foreground">
                {(l.created_at || "").slice(0, 10)}
              </td>
              <td className="py-3 pr-4 text-muted-foreground">
                {(l.sources || [l.source]).map((s) => SOURCE_LABEL[s] || s).join(", ")}
              </td>
              <td className="py-3 pr-4 text-primary">{(l.purchases || []).join(", ") || "—"}</td>
              <td className="mono py-3 pr-4 text-[11px] text-muted-foreground">
                LEVEL {l.highest_offer_level || 0}
              </td>
              <td className="mono py-3 pr-4 text-[11px]">
                {l.nurture_day === null || l.nurture_day === undefined ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <span className={l.nurture_complete ? "text-muted-foreground" : "text-primary"}>
                    {l.nurture_complete ? "SEQUENCE DONE" : `DAY ${l.nurture_day}`}
                  </span>
                )}
              </td>
              <td className="py-3 pr-4">
                {l.operator_interest ? <span className="text-primary">YES</span> : "—"}
              </td>
              <td className="py-3">
                <Select
                  testId={`hq-lead-status-${l.email}`}
                  value={l.status}
                  onChange={(v) => setStatus(l, v)}
                  options={data.statuses}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export const OperatorsRoster = () => {
  const { content } = useData();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/operators")
      .then(({ data }) => setRows(data.operators))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message));
  }, []);

  if (error) return <div className="text-sm text-destructive">{error}</div>;
  if (!rows) return <div className="mono text-sm text-muted-foreground">Loading operators…</div>;

  const currentMilestone = (state) => {
    const m = MILESTONES.find((x) => {
      const { done, total } = milestoneProgress(x, content, state);
      return done < total;
    });
    return m ? `${m.n} · ${m.title}` : "All complete";
  };

  return (
    <div className="overflow-x-auto" data-testid="hq-operators">
      <table className="w-full min-w-[820px] border-collapse text-sm">
        <thead>
          <tr className="mono border-b border-border text-left text-[10px] tracking-widest text-muted-foreground">
            <th className="py-3 pr-4">NAME</th>
            <th className="py-3 pr-4">EMAIL</th>
            <th className="py-3 pr-4">ACCESS</th>
            <th className="py-3 pr-4">PROGRESS</th>
            <th className="py-3 pr-4">CURRENT MILESTONE</th>
            <th className="py-3">LAST ACTIVE</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => {
            const state = { completedSteps: [], certifications: {}, checklist: [], ...o.state };
            const paid = o.role === "admin" || (o.entitlements || []).includes("popup_profits");
            return (
              <tr key={o.id} data-testid={`hq-operator-${o.email}`} className="border-b border-border/60">
                <td className="py-3 pr-4">{o.name || "—"}</td>
                <td className="py-3 pr-4 text-muted-foreground">{o.email}</td>
                <td className="py-3 pr-4">
                  <span className={paid ? "text-primary" : "text-muted-foreground"}>
                    {o.role === "admin" ? "HQ ADMIN" : paid ? "PAID" : "FREE PREVIEW"}
                  </span>
                </td>
                <td className="display py-3 pr-4 text-xl text-primary">
                  {overallPercent(content, state)}%
                </td>
                <td className="py-3 pr-4 text-muted-foreground">{currentMilestone(state)}</td>
                <td className="mono py-3 text-[11px] text-muted-foreground">
                  {(o.last_active || o.created_at || "").slice(0, 16).replace("T", " ") || "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
